"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";

function parseFilterHeadline(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(un profil de fondateur très précis|vôtre)/i);
    return parts.map((part, i) => {
      if (/un profil de fondateur très précis/i.test(part)) {
        return <span key={i} className="text-gold">{part}</span>;
      }
      if (/vôtre/i.test(part)) {
        return <span key={i} className="italic">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(one specific founder|you)/i);
  return parts.map((part, i) => {
    if (/one specific founder/i.test(part)) {
      return <span key={i} className="text-gold">{part}</span>;
    }
    if (/you/i.test(part)) {
      return <span key={i} className="italic">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export const InstantFilter = () => {
  const { t, lang } = useV2Translation();

  return (
    <section
      id="filter"
      data-testid="filter-section"
      className="py-24 lg:py-32 border-t border-line"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16 lg:mb-20">
          <div className="col-span-12">
            <h2
              data-testid="filter-headline"
              className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight max-w-[22ch]"
            >
              {parseFilterHeadline(t.filter.headline, lang)}
            </h2>
          </div>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-navy"
          style={{ perspective: 1400 }}
        >
          {/* Column A */}
          <motion.div
            data-testid="filter-yes-column"
            initial={{ opacity: 0, x: -40, rotateY: 8 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="bg-navy text-paper p-8 lg:p-12 relative"
          >
            <div className="flex items-center justify-between mb-10">
              <p className="font-mono text-[10px] uppercase tracking-widest text-paper/70">
                {t.filter.yesLabel}
              </p>
              <span className="font-display text-3xl italic text-gold">
                {t.filter.yesLetter}
              </span>
            </div>
            <h3 className="font-display text-2xl lg:text-4xl leading-tight mb-10">
              {t.filter.yesTitle}
            </h3>
            <ul className="space-y-6">
              {t.filter.yesItems.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-4 items-start"
                  data-testid={`filter-yes-${i}`}
                >
                  <span className="mt-1.5 inline-flex items-center justify-center w-5 h-5 border border-gold shrink-0">
                    <Check size={12} className="text-gold" strokeWidth={3} />
                  </span>
                  <p className="font-body text-[15px] lg:text-base text-paper/85 leading-relaxed">
                    {line}
                  </p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Column B */}
          <motion.div
            data-testid="filter-no-column"
            initial={{ opacity: 0, x: 40, rotateY: -8 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.85,
              delay: 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="bg-paper-pure p-8 lg:p-12 border-t lg:border-t-0 lg:border-l border-navy"
          >
            <div className="flex items-center justify-between mb-10">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#6b6860]">
                {t.filter.noLabel}
              </p>
              <span className="font-display text-3xl italic text-ink/30">
                {t.filter.noLetter}
              </span>
            </div>
            <h3 className="font-display text-2xl lg:text-4xl leading-tight mb-10">
              {t.filter.noTitle}
            </h3>
            <ul className="space-y-6">
              {t.filter.noItems.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-4 items-start"
                  data-testid={`filter-no-${i}`}
                >
                  <span className="mt-1.5 inline-flex items-center justify-center w-5 h-5 border border-ink/40 shrink-0">
                    <X
                      size={12}
                      className="text-ink/70"
                      strokeWidth={3}
                    />
                  </span>
                  <p className="font-body text-[15px] lg:text-base text-ink/75 leading-relaxed line-through decoration-gold/60 decoration-[1.5px]">
                    {line}
                  </p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
